import { useCallback, useEffect, useRef } from 'react';
import { Portal, Group, Text, CloseButton } from '@mantine/core';
import { useFloatingWindow, useLocalStorage, useMergedRef } from '@mantine/hooks';
import { IconGripVertical } from '@tabler/icons-react';
import { NotesRichText } from './NotesRichText';
import classes from './ReportNotesWindow.module.css';

interface WindowRect {
  x: number | null;
  y: number | null;
  w: number;
  h: number;
}

const DEFAULT_RECT: WindowRect = { x: null, y: null, w: 400, h: 340 };

interface ReportNotesWindowProps {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled: boolean;
  onClose: () => void;
}

export function ReportNotesWindow({ value, onChange, disabled, onClose }: ReportNotesWindowProps) {
  const [rect, setRect] = useLocalStorage<WindowRect>({
    key: 'report-notes-window',
    defaultValue: DEFAULT_RECT,
    getInitialValueInEffect: false,
  });

  const latestPos = useRef<{ x: number; y: number } | null>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);

  // Stable callbacks so the hook's drag listeners aren't re-bound on every render.
  const handlePositionChange = useCallback(({ x, y }: { x: number; y: number }) => {
    latestPos.current = { x, y };
  }, []);
  const handleDragEnd = useCallback(() => {
    if (latestPos.current) {
      const { x, y } = latestPos.current;
      setRect((prev) => ({ ...prev, x, y }));
    }
  }, [setRect]);

  const { ref } = useFloatingWindow<HTMLDivElement>({
    dragHandleSelector: `.${classes.header}`,
    constrainToViewport: true,
    constrainOffset: 8,
    initialPosition: rect.x != null && rect.y != null ? { left: rect.x, top: rect.y } : undefined,
    onPositionChange: handlePositionChange,
    onDragEnd: handleDragEnd,
  });

  // Merge the hook's ref with our own element ref into ONE stable callback. An
  // inline ref function would change identity every render, making React detach
  // and re-attach the node — which re-runs the hook's init effect and resets the
  // window position mid-interaction (the cause of "drag only works on 2nd try").
  const mergedRef = useMergedRef(ref, elementRef);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const border = entry.borderBoxSize?.[0];
      const w = border ? border.inlineSize : el.offsetWidth;
      const h = border ? border.blockSize : el.offsetHeight;
      setRect((prev) => ({ ...prev, w: Math.round(w), h: Math.round(h) }));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [setRect]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <Portal>
      <div
        ref={mergedRef}
        className={classes.window}
        style={{ width: rect.w, height: rect.h }}
        role="dialog"
        aria-label="Notizen"
      >
        <div className={classes.header}>
          <Group gap={6} wrap="nowrap">
            <IconGripVertical size={16} opacity={0.6} />
            <Text size="sm" fw={700}>
              Notizen
            </Text>
          </Group>
          <CloseButton onClick={onClose} aria-label="Notizen schließen" />
        </div>
        <div className={classes.body}>
          <NotesRichText value={value} onChange={onChange} disabled={disabled} />
        </div>
      </div>
    </Portal>
  );
}
